import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  AddCommentFormData,
  addCommentSchema,
} from "@/features/recordings/types/add-comment-schema";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { cn } from "@/lib/utils";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicRecordingComment } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useCreateRecordingComment } from "../api/add-recording-comment";
import { fetchRecordingCommentsKey } from "../api/fetch-recording-comments";

type AddCommentProps = {
  parentCommentId?: string;
  className?: string;
};

export function AddComment(props: AddCommentProps) {
  const { parentCommentId, className } = props;
  const { id: recordingId } = useParams();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const form = useForm<AddCommentFormData>({
    resolver: zodResolver(addCommentSchema),
    defaultValues: {
      content: "",
    },
  });

  const { organisation } = useOrganisation();

  const { mutateAsync: createComment } = useCreateRecordingComment(
    recordingId || "",
    {
      onSuccess: (comment) => {
        queryClient.setQueryData(
          fetchRecordingCommentsKey({
            organisationId: organisation.id,
            recordingId: recordingId || "",
          }),
          (old: PublicRecordingComment[]) => [comment, ...old],
        );
      },
    },
  );

  useEffect(() => {
    if (isFormOpen) {
      form.reset();
    }
  }, [isFormOpen, form]);

  if (!organisation.canComment) {
    return null;
  }

  if (!isFormOpen && !parentCommentId) {
    return (
      <button
        type="button"
        className="w-full rounded-md p-4 text-left text-sm text-muted-foreground hover:text-foreground"
        onClick={() => setIsFormOpen(true)}
      >
        Add a comment...
      </button>
    );
  }

  if (!isFormOpen && parentCommentId) {
    return (
      <Button
        variant="ghost"
        className="h-auto p-0 hover:bg-transparent"
        onClick={() => setIsFormOpen(true)}
      >
        Reply
      </Button>
    );
  }

  const handleCreateComment = async (data: AddCommentFormData) => {
    try {
      await createComment({
        content: data.content,
        parentCommentId: parentCommentId || undefined,
      });

      setIsFormOpen(false);
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  };

  return (
    <Form {...form}>
      <form
        className={cn("flex w-full flex-col gap-2 p-4", className)}
        onSubmit={form.handleSubmit(handleCreateComment)}
      >
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem className="flex flex-1 flex-col">
              <Input
                autoFocus
                placeholder="Add your comment.."
                {...field}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setIsFormOpen(false);
                  }
                }}
              />
              <FormMessage />
            </FormItem>
          )}
        />

        {form.formState.errors.root && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>
              {form.formState.errors.root.message}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsFormOpen(false)}
          >
            Cancel
          </Button>
          <Button
            isLoading={form.formState.isSubmitting}
            isAnimated={form.formState.isSubmitting}
            disabled={form.formState.isSubmitting || !form.formState.isValid}
          >
            Add Comment
          </Button>
        </div>
      </form>
    </Form>
  );
}
